var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NotificationPosition, useNotificationsMessage, useRepositories, } from '../../contexts';
import { useGetCustomerApi } from '../../hooks';
import { InvalidationTag, LoaderType, useAppMutation, } from '../../hooks/api/utils';
import { useLanguage } from '../../locale';
import { EN, IT, US } from '../../locale/localizationContext';
import { CustomerStatus, LanguagesOptions, UnitOfMeasureOptions, } from '../../models';
import { isAuthorizedSelector } from '../../redux';
import { useQueryClient } from '@tanstack/react-query';
// Versione personalizzata per il cambio lingua senza loader.
// Nessun onSuccess/invalidate qui: l'invalidazione viene fatta nel facade in finally,
// così il refetch Customer non parte durante il salvataggio e non fa comparire un secondo loader (full-page).
var useUpdateCustomerApiNoLoader = function () {
    var customerRepo = useRepositories().customerRepo;
    return useAppMutation(function (params) { return customerRepo.updateCustomer(params); }, {}, LoaderType.None);
};
export var useLanguageAndUnitOfMeasureFacade = function (onLanguageUpdate) {
    var _a;
    var _b = useLanguage(), t = _b[0], setLocale = _b[1], locale = _b[2];
    var showNotifications = useNotificationsMessage().showNotifications;
    var queryClient = useQueryClient();
    var isAuthenticated = useSelector(isAuthorizedSelector);
    var customer = useGetCustomerApi(Boolean(isAuthenticated)).data;
    var _c = useUpdateCustomerApiNoLoader(), updateLanguageAndUnitOfMeasureApi = _c.mutateAsync, isLoading = _c.isLoading;
    // Calcola la lingua iniziale: usa il customer se disponibile, altrimenti il locale corrente
    var initialLanguage = useMemo(function () {
        if (customer === null || customer === void 0 ? void 0 : customer.language) {
            return customer.language;
        }
        // Se il customer non è disponibile, usa il locale corrente convertito in formato LanguagesOptions
        switch (locale) {
            case EN:
                return LanguagesOptions.EnglishUK;
            case US:
                return LanguagesOptions.EnglishUS;
            case IT:
            default:
                return LanguagesOptions.Italian;
        }
    }, [customer === null || customer === void 0 ? void 0 : customer.language, locale]);
    var _d = useState(initialLanguage), selectedLanguage = _d[0], setSelectedLanguage = _d[1];
    var _e = useState((_a = customer === null || customer === void 0 ? void 0 : customer.unitSystem) !== null && _a !== void 0 ? _a : UnitOfMeasureOptions.metric), selectedUnitOfMeasure = _e[0], setSelectedUnitOfMeasure = _e[1];
    var _f = useState(false), isSaving = _f[0], setIsSaving = _f[1];
    useEffect(function () {
        if (customer === null || customer === void 0 ? void 0 : customer.language) {
            switch (customer.language) {
                case LanguagesOptions.EnglishUK:
                    setLocale(EN, false);
                    break;
                case LanguagesOptions.EnglishUS:
                    setLocale(US, false);
                    break;
                default:
                    setLocale(IT, false);
            }
        }
    }, [customer === null || customer === void 0 ? void 0 : customer.language, setLocale]);
    useEffect(function () {
        // Aggiorna selectedLanguage quando il customer viene aggiornato
        if (customer === null || customer === void 0 ? void 0 : customer.language) {
            setSelectedLanguage(customer.language);
        }
        else {
            // Se il customer non ha ancora la lingua, usa il locale corrente
            switch (locale) {
                case EN:
                    setSelectedLanguage(LanguagesOptions.EnglishUK);
                    break;
                case US:
                    setSelectedLanguage(LanguagesOptions.EnglishUS);
                    break;
                case IT:
                default:
                    setSelectedLanguage(LanguagesOptions.Italian);
                    break;
            }
        }
    }, [customer === null || customer === void 0 ? void 0 : customer.language, locale]);
    useEffect(function () {
        if (customer === null || customer === void 0 ? void 0 : customer.unitSystem) {
            setSelectedUnitOfMeasure(customer.unitSystem);
        }
    }, [customer === null || customer === void 0 ? void 0 : customer.unitSystem]);
    var isButtonDisabled = useMemo(function () {
        var _a;
        var languageUnchanged = selectedLanguage === (customer === null || customer === void 0 ? void 0 : customer.language);
        var unitUnchanged = selectedUnitOfMeasure ===
            ((_a = customer === null || customer === void 0 ? void 0 : customer.unitSystem) !== null && _a !== void 0 ? _a : UnitOfMeasureOptions.metric);
        var hasNoChange = languageUnchanged && unitUnchanged;
        return !selectedLanguage || hasNoChange || isLoading || isSaving;
    }, [
        customer === null || customer === void 0 ? void 0 : customer.language,
        customer === null || customer === void 0 ? void 0 : customer.unitSystem,
        selectedLanguage,
        selectedUnitOfMeasure,
        isLoading,
        isSaving,
    ]);
    var handleLanguageChange = function (language) {
        setSelectedLanguage(language);
    };
    var handleUnitOfMeasureChange = function (unit) {
        setSelectedUnitOfMeasure(unit);
    };
    var onPressSaveBtn = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var targetLocaleCode, updateError_1, e_1;
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!customer) {
                        return [2 /*return*/];
                    }
                    // Attiva il loader immediatamente
                    setIsSaving(true);
                    switch (selectedLanguage) {
                        case LanguagesOptions.EnglishUK:
                            targetLocaleCode = EN;
                            break;
                        case LanguagesOptions.EnglishUS:
                            targetLocaleCode = US;
                            break;
                        default:
                            targetLocaleCode = IT;
                    }
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 7, 8, 9]);
                    return [4 /*yield*/, updateLanguageAndUnitOfMeasureApi({
                            id: customer.id,
                            payload: {
                                countryId: (_a = customer.countryId) !== null && _a !== void 0 ? _a : undefined,
                                privacy: customer.privacy,
                                status: CustomerStatus.Active,
                                newsletter: customer.newsletter,
                                language: selectedLanguage,
                                unitSystem: selectedUnitOfMeasure,
                            },
                        })];
                case 2:
                    _b.sent();
                    if (!onLanguageUpdate) return [3 /*break*/, 6];
                    _b.label = 3;
                case 3:
                    _b.trys.push([3, 5, , 6]);
                    return [4 /*yield*/, onLanguageUpdate(targetLocaleCode)];
                case 4:
                    _b.sent();
                    return [3 /*break*/, 6];
                case 5:
                    updateError_1 = _b.sent();
                    return [3 /*break*/, 6];
                case 6:
                    // Aggiorna la lingua locale
                    switch (selectedLanguage) {
                        case LanguagesOptions.EnglishUK:
                            setLocale(EN, true);
                            break;
                        case LanguagesOptions.EnglishUS:
                            setLocale(US, true);
                            break;
                        default:
                            setLocale(IT, true);
                    }
                    return [3 /*break*/, 9];
                case 7:
                    e_1 = _b.sent();
                    showNotifications({
                        text: t('profilePage.languageAndUnitOfMeasurePage.errorInSavingChanges'),
                        position: NotificationPosition.bottom,
                        isError: true,
                    });
                    return [3 /*break*/, 9];
                case 8:
                    void queryClient.invalidateQueries([InvalidationTag.Customer]);
                    void queryClient.invalidateQueries([InvalidationTag.Recipe]);
                    void queryClient.invalidateQueries([InvalidationTag.ShoppingList]);
                    setIsSaving(false);
                    return [7 /*endfinally*/];
                case 9: return [2 /*return*/];
            }
        });
    }); }, [
        customer,
        selectedLanguage,
        selectedUnitOfMeasure,
        queryClient,
        setLocale,
        showNotifications,
        t,
        updateLanguageAndUnitOfMeasureApi,
        onLanguageUpdate,
    ]);
    return {
        isButtonDisabled: isButtonDisabled,
        selectedLanguage: selectedLanguage,
        selectedUnitOfMeasure: selectedUnitOfMeasure,
        setSelectedLanguage: setSelectedLanguage,
        setSelectedUnitOfMeasure: setSelectedUnitOfMeasure,
        handleLanguageChange: handleLanguageChange,
        handleUnitOfMeasureChange: handleUnitOfMeasureChange,
        onPressSaveBtn: onPressSaveBtn,
        t: t,
        isLoading: isLoading,
        isSaving: isSaving,
    };
};
